import {useCallback, useRef, useState} from 'react'
import {StyleSheet, View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import type React from 'react';

import {type AttachmentToPick} from '#/state/matrix/types'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'

/**
 * Drag-and-drop file upload overlay for web.
 * Wraps children and shows a dropzone overlay when files are dragged over.
 */
export function SecureChatDropTarget({
  children,
  onDropFiles,
}: {
  children: React.ReactNode
  onDropFiles: (attachments: AttachmentToPick[]) => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)

  const hasFiles = (dataTransfer: DataTransfer) => {
    return (
      dataTransfer.types.includes('Files') ||
      dataTransfer.types.includes('application/x-moz-file')
    )
  }

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (!hasFiles(e.dataTransfer)) return
    dragCounter.current++
    if (dragCounter.current === 1) {
      setIsDragging(true)
    }
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      dragCounter.current = 0
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files).filter(
        f => f.type.startsWith('image/') || f.type.startsWith('video/'),
      )
      if (!files.length) return

      const attachments = await Promise.all(
        files.map(
          f =>
            new Promise<AttachmentToPick>(resolve => {
              const uri = URL.createObjectURL(f)
              if (f.type.startsWith('image/')) {
                const img = new Image()
                img.onload = () =>
                  resolve({
                    uri,
                    name: f.name,
                    mime: f.type,
                    size: f.size,
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                  })
                img.onerror = () =>
                  resolve({
                    uri,
                    name: f.name,
                    mime: f.type,
                    size: f.size,
                    width: 0,
                    height: 0,
                  })
                img.src = uri
              } else {
                resolve({
                  uri,
                  name: f.name,
                  mime: f.type,
                  size: f.size,
                  width: 0,
                  height: 0,
                })
              }
            }),
        ),
      )
      onDropFiles(attachments)
    },
    [onDropFiles],
  )

  return (
    <View
      style={[a.flex_1]}
      // @ts-expect-error web-only drag events
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}>
      {children}
      {isDragging && (
        <View
          style={[
            StyleSheet.absoluteFill,
            a.align_center,
            a.justify_center,
            {backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 100},
          ]}>
          <View
            style={[
              a.rounded_lg,
              a.p_xl,
              t.atoms.bg,
              a.align_center,
              {
                borderWidth: 2,
                borderStyle: 'dashed',
                borderColor: t.palette.primary_500,
              },
            ]}>
            <Text style={[a.text_lg, a.font_bold]}>
              {_(msg`Drop files to send`)}
            </Text>
          </View>
        </View>
      )}
    </View>
  )
}
